/**
 * OPDS HTTP Client
 *
 * Fetches OPDS feeds and page streaming images
 */

import axios, { type AxiosInstance } from 'axios';
import { parseOPDSFeed, buildPageStreamUrl, parseOpenSearchDescription, buildSearchUrl } from './parser.js';
import type { OPDSFeed, OPDSLink, PageStreamOptions, OpenSearchDescription, OPDSSearchParams } from './types.js';

/**
 * OPDS Client Options
 */
export interface OPDSClientOptions {
  baseUrl?: string;
  headers?: Record<string, string>;
  timeout?: number;
}

/**
 * OPDS Client
 */
export class OPDSClient {
  private axios: AxiosInstance;

  constructor(options: OPDSClientOptions = {}) {
    this.axios = axios.create({
      baseURL: options.baseUrl || '',
      headers: options.headers || {},
      timeout: options.timeout || 30000,
      validateStatus: (status) => status >= 200 && status < 300,
    });
  }

  /**
   * Fetch OPDS feed from URL
   */
  async fetchFeed(url: string): Promise<OPDSFeed> {
    const response = await this.axios.get(url, {
      headers: {
        Accept: 'application/atom+xml, application/xml, text/xml',
      },
      responseType: 'text',
    });

    const absoluteUrl = url.startsWith('http') ? url : new URL(url, this.axios.defaults.baseURL || '').toString();
    return parseOPDSFeed(response.data, { baseUrl: new URL(absoluteUrl).origin });
  }

  /**
   * Fetch page from page streaming link
   */
  async fetchPage(link: OPDSLink, options: PageStreamOptions): Promise<Blob> {
    const url = buildPageStreamUrl(link, options.pageNumber, options.maxWidth);

    const response = await this.axios.get(url, {
      responseType: 'arraybuffer',
    });

    // Get content type from response headers or link type
    const contentType = response.headers['content-type'] || link.type || 'application/octet-stream';

    // Convert ArrayBuffer to Blob
    return new Blob([response.data], { type: contentType });
  }

  /**
   * Fetch page as data URL
   */
  async fetchPageAsDataUrl(link: OPDSLink, options: PageStreamOptions): Promise<string> {
    const blob = await this.fetchPage(link, options);

    // Use Buffer for Node.js/Bun environments
    const buffer = await blob.arrayBuffer();
    const base64 = Buffer.from(buffer).toString('base64');
    return `data:${blob.type};base64,${base64}`;
  }

  /**
   * Fetch OpenSearch description document
   */
  async fetchOpenSearchDescription(url: string): Promise<OpenSearchDescription> {
    const response = await this.axios.get(url, {
      headers: {
        Accept: 'application/opensearchdescription+xml, application/xml, text/xml',
      },
      responseType: 'text',
    });

    return parseOpenSearchDescription(response.data);
  }

  /**
   * Search using OpenSearch descriptor
   */
  async search(searchDescriptorUrl: string, params: OPDSSearchParams): Promise<OPDSFeed> {
    const descriptor = await this.fetchOpenSearchDescription(searchDescriptorUrl);

    // Find OPDS search URL template
    const opdsUrl = descriptor.url.find(
      (u) => u.type === 'application/atom+xml' || u.type === 'application/atom+xml;profile=opds-catalog'
    );

    if (!opdsUrl) {
      throw new Error('No OPDS search URL found in OpenSearch descriptor');
    }

    const searchUrl = buildSearchUrl(opdsUrl.template, params);
    return this.fetchFeed(searchUrl);
  }
}
